import { useEffect, useState, useCallback } from 'react';
import { wsClient } from '../services/websocket/WebSocketClient';
import { ConnectionState } from '../services/websocket/types';
import { useAppSelector } from '../store/hooks';

/**
 * Hook to manage WebSocket connection with authentication
 * Automatically connects when user is authenticated and disconnects on logout
 */
export function useWebSocket() {
    const token = useAppSelector(state => state.auth.user?.accessToken);
    const [connectionState, setConnectionState] = useState<ConnectionState>(
        wsClient.getState()
    );
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Set up state change listener
        const unsubscribeState = wsClient.onStateChange((state) => {
            setConnectionState(state);
        });

        // Connect if user has token and not already connected
        if (token && connectionState === ConnectionState.DISCONNECTED) {
            console.log('[useWebSocket] Connecting to WebSocket server...');
            wsClient.connect(token).catch((err) => {
                console.error('[useWebSocket] Connection failed:', err);
                setError(err.message);
            });
        }

        // Disconnect if token is removed (user logged out)
        if (!token && connectionState !== ConnectionState.DISCONNECTED) {
            console.log('[useWebSocket] Disconnecting from WebSocket server...');
            wsClient.disconnect();
        }

        return () => {
            unsubscribeState();
        };
    }, [token, connectionState]);

    const subscribe = useCallback((channels: string[]) => {
        return wsClient.subscribe(channels);
    }, []);

    const unsubscribe = useCallback((channels: string[]) => {
        wsClient.unsubscribe(channels);
    }, []);

    const configureProvider = useCallback((providerType: string, credentials: any) => {
        return wsClient.configureProvider(providerType, credentials);
    }, []);

    const onChannelData = useCallback((channel: string, handler: (data: any) => void) => {
        return wsClient.onChannelData(channel, handler);
    }, []);

    return {
        connectionState,
        isConnected: connectionState === ConnectionState.CONNECTED,
        error,
        subscribe,
        unsubscribe,
        configureProvider,
        onChannelData
    };
}
